import { Link } from "react-router-dom";
import { US_STATES, CARIBBEAN_LOCATIONS, locationToSlug } from "@/lib/constants";
import { Globe, MapPin } from "lucide-react";

const allStates = Object.values(US_STATES);

// Regional groupings for organized display
const DESTINATION_GROUPS = [
  {
    title: "Caribbean",
    locations: CARIBBEAN_LOCATIONS.map(loc => ({ name: loc.name, slug: locationToSlug(loc.name) }))
  },
  {
    title: "Northeast",
    locations: allStates
      .filter(s => ["maine", "massachusetts", "rhode-island", "connecticut", "new-york", "new-jersey", "pennsylvania", "delaware", "new-hampshire", "vermont"].includes(s.slug))
      .map(s => ({ name: s.name, slug: s.slug, comingSoon: s.comingSoon }))
  },
  {
    title: "Mid-Atlantic",
    locations: allStates
      .filter(s => ["maryland", "washington-dc", "virginia", "west-virginia"].includes(s.slug))
      .map(s => ({ name: s.name, slug: s.slug, comingSoon: s.comingSoon }))
  },
  {
    title: "Southeast",
    locations: allStates
      .filter(s => ["north-carolina", "south-carolina", "georgia", "florida", "alabama", "mississippi", "louisiana", "tennessee", "kentucky"].includes(s.slug))
      .map(s => ({ name: s.name, slug: s.slug, comingSoon: s.comingSoon }))
  },
  {
    title: "Midwest",
    locations: allStates
      .filter(s => ["ohio", "michigan", "illinois", "indiana", "wisconsin", "minnesota", "iowa", "missouri", "kansas", "nebraska", "north-dakota", "south-dakota"].includes(s.slug))
      .map(s => ({ name: s.name, slug: s.slug, comingSoon: s.comingSoon }))
  },
  {
    title: "Southwest",
    locations: allStates
      .filter(s => ["texas", "oklahoma", "arkansas", "arizona", "new-mexico"].includes(s.slug))
      .map(s => ({ name: s.name, slug: s.slug, comingSoon: s.comingSoon }))
  },
  {
    title: "West",
    locations: allStates
      .filter(s => ["california", "nevada", "oregon", "washington", "colorado", "utah", "idaho", "montana", "wyoming", "alaska", "hawaii"].includes(s.slug))
      .map(s => ({ name: s.name, slug: s.slug, comingSoon: s.comingSoon }))
  }
];

export default function Footer() {
  return (
    <footer className="bg-ink text-white mt-auto" data-testid="footer">
      {/* Main Destinations Section */}
      <div className="px-6 md:px-12 lg:px-16 py-16 lg:py-20">
        {/* Brand Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16 pb-10 border-b border-white/10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src="/logo.png" alt="Paradise Borrowed" className="h-12 w-auto invert brightness-200" />
              <span className="font-display text-2xl tracking-tight">Paradise Borrowed<sup className="text-xs ml-1">™</sup></span>
            </div>
            <p className="text-stone-400 text-base max-w-md leading-relaxed font-ui">
              Everything you need is already there.
            </p>
          </div>
        </div>

        {/* Destinations Grid - The Hero of the Footer */}
        <div className="mb-16">
          <div className="flex items-center gap-2 mb-8">
            <MapPin className="w-5 h-5 text-coral" />
            <h3 className="font-display text-lg text-white tracking-wide">Explore Destinations</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-x-6 gap-y-10">
            {DESTINATION_GROUPS.map((group) => (
              <div key={group.title}>
                <h4 className="text-coral text-xs font-ui uppercase tracking-widest mb-4">{group.title}</h4>
                <ul className="space-y-2">
                  {group.locations.map((loc) => (
                    <li key={loc.slug}>
                      <Link 
                        to={`/${loc.slug}`}
                        className="text-stone-400 hover:text-white hover:translate-x-1 inline-flex items-center gap-1.5 transition-all duration-200 text-sm font-ui"
                        data-testid={`footer-link-${loc.slug}`}
                      >
                        {loc.name}
                        {loc.comingSoon && <span className="text-[10px] text-coral/70">soon</span>}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Categories, Company, Legal Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-10 border-t border-white/10">
          <div>
            <h4 className="font-display text-sm text-white mb-4">Categories</h4>
            <ul className="space-y-2.5">
              <li><Link to="/browse?category=Water+Sports" className="text-stone-400 hover:text-white text-sm font-ui transition-colors">Water Sports</Link></li>
              <li><Link to="/browse?category=Baby+%26+Kids" className="text-stone-400 hover:text-white text-sm font-ui transition-colors">Baby & Kids</Link></li>
              <li><Link to="/browse?category=Photography+%26+Video" className="text-stone-400 hover:text-white text-sm font-ui transition-colors">Photography & Video</Link></li>
              <li><Link to="/browse?category=Household+Appliances" className="text-stone-400 hover:text-white text-sm font-ui transition-colors">Household Appliances</Link></li>
              <li><Link to="/browse?category=Tools+%26+Equipment" className="text-stone-400 hover:text-white text-sm font-ui transition-colors">Tools & Equipment</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display text-sm text-white mb-4">Company</h4>
            <ul className="space-y-2.5">
              <li><Link to="/about" className="text-stone-400 hover:text-white text-sm font-ui transition-colors" data-testid="footer-link-about">About Us</Link></li>
              <li><Link to="/how-it-works" className="text-stone-400 hover:text-white text-sm font-ui transition-colors">How It Works</Link></li>
              <li><Link to="/fees" className="text-stone-400 hover:text-white text-sm font-ui transition-colors">Platform Fees</Link></li>
              <li><Link to="/trust-and-safety" className="text-stone-400 hover:text-white text-sm font-ui transition-colors">Trust & Safety</Link></li>
              <li><Link to="/advertise" className="text-stone-400 hover:text-white text-sm font-ui transition-colors">Advertise</Link></li>
              <li><Link to="/sitemap" className="text-stone-400 hover:text-white text-sm font-ui transition-colors">Sitemap</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display text-sm text-white mb-4">Legal</h4>
            <ul className="space-y-2.5">
              <li><Link to="/terms-of-service" className="text-stone-400 hover:text-white text-sm font-ui transition-colors">Terms of Service</Link></li>
              <li><Link to="/privacy-policy" className="text-stone-400 hover:text-white text-sm font-ui transition-colors">Privacy Policy</Link></li>
              <li><Link to="/trust" className="text-stone-400 hover:text-white text-sm font-ui transition-colors">Dispute Resolution</Link></li>
              <li><Link to="/trust-and-safety" className="text-stone-400 hover:text-white text-sm font-ui transition-colors">Trust & Safety</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display text-sm text-white mb-4">Support</h4>
            <ul className="space-y-2.5">
              <li><Link to="/contact" className="text-stone-400 hover:text-white text-sm font-ui transition-colors" data-testid="footer-link-contact">Contact Us</Link></li>
              <li><Link to="/help-center" className="text-stone-400 hover:text-white text-sm font-ui transition-colors">Help Center</Link></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 px-6 md:px-12 lg:px-16 py-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-stone-500 text-xs font-ui">
            <span>&copy; 2026 ParadiseBorrowed.com</span>
          </div>
          <div className="flex items-center gap-2 text-stone-500 text-xs">
            <Globe className="w-4 h-4" />
            <span className="font-ui">English (US)</span>
            <span className="ml-2 font-ui">$ USD</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
